import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Chip, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, IconButton, Paper, Alert, CircularProgress,
} from "@mui/material";
import { Visibility as EyeIcon, Edit as EditIcon } from "@mui/icons-material";
import { Download as DownloadIcon } from "@mui/icons-material";
import {
  GET_INSTALLATION_ENDPOINT, GET_DASHBOARD_STATS_ENDPOINT,
  PUT_INSTALLATION_ENDPOINT, EXPORT_INSTALLATION_ENDPOINT,
  GET_INSTALLATION_CHOICES_ENDPOINT,
} from "../../config/urls";
import DoorLocksFeatureLayout from "./DoorLocksFeatureLayout";
import { InstallationEditModal } from "./EditModals";
import dayjs from "dayjs";
import { formatWithTzLabel } from "../../utils/dateUtils";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import AppDropdown from "../../common/app-dropdown";
import AppDatePicker from "../../common/app-date-picker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { grayFontColor } from "../../cssUtils/constants";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";

function getStatusColor(status) {
  const colors = {
    New: "primary", "In Progress": "warning", Scheduled: "info",
    Completed: "success", Pending: "warning", Cancelled: "error",
  };
  return colors[status] || "default";
}

const LOCK_TYPE_OPTIONS = [
  { label: "All Lock Types", value: ""           },
  { label: "XL-C 4605",      value: "XL-C 4605"  },
  { label: "XL-C 4606",      value: "XL-C 4606"  },
  { label: "XL-C 4607",      value: "XL-C 4607"  },
  { label: "XL-C 4608",      value: "XL-C 4608"  },
  { label: "XL-C 4609",      value: "XL-C 4609"  },
  { label: "XL-C 4610",      value: "XL-C 4610"  },
  { label: "Others",          value: "Others"     },
];

export default function InstallationTab({ activeTab = 0, counts = {}, onTabChange, onBack, onNavigate }) {
  const [installationsData, setInstallationsData] = useState([]);
  const [totalCount, setTotalCount]               = useState(0);
  const [loadingTable, setLoadingTable]           = useState(false);
  const [error, setError]                         = useState(null);
  const [editError, setEditError]                 = useState(null);
  const [editSuccess, setEditSuccess]             = useState(false);
  const [stats, setStats]                         = useState(null);
  const [loadingStats, setLoadingStats]           = useState(true);
  const [exporting, setExporting]                 = useState(false);
  const [charCount, setCharCount]                 = useState(0);
  const [page, setPage]                           = useState(0);
  const [rowsPerPage, setRowsPerPage]             = useState(20);

  // Filters
  const [searchQuery, setSearchQuery]       = useState("");
  const [cityFilter, setCityFilter]         = useState("");
  const [lockTypeFilter, setLockTypeFilter] = useState("");
  const [statusFilter, setStatusFilter]     = useState("");
  const [updatedByFilter, setUpdatedByFilter]     = useState("");
  const [updatedDateFilter, setUpdatedDateFilter] = useState("");

  // Filter dropdown choices (city / status / updated_by), fetched from installation_choices
  const [filterChoices, setFilterChoices] = useState({ cities: [], statuses: [], updated_by: [] });

  // Modals
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalOpen, setModalOpen]           = useState(false);
  const [editModalOpen, setEditModalOpen]   = useState(false);

  const [tab, setTab] = useState(activeTab);
  const handleTabChange = onTabChange || ((e, newValue) => {
    setTab(newValue);
    if (onNavigate) {
      const tabKeyMap = ["installation", "service", "sales", "billing_dashboard"];
      newValue === 3 ? onNavigate("insights") : onNavigate(tabKeyMap[newValue] || "installation");
    }
  });
  const handleBack = onBack || (() => { onNavigate ? onNavigate("insights") : window.history.back(); });

  const [editFormData, setEditFormData] = useState({
    ticket_code: "", customer_country_code: "", customer_phone: "", wa_number: "",
    customer_name: "", wa_name: "", lock_type: "", lock_type_others: "", status: "",
    door_type: "", door_installed: false, scheduled_date: "",
    address: { flat: "", street: "", area: "", pincode: "", city: "", state: "", country: "" },
    notes: "",
  });

  const getToken = () => localStorage.getItem("token");

  // ── Stats ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    setLoadingStats(true);
    fetch(GET_DASHBOARD_STATS_ENDPOINT, { headers: { "X-Tenant-ID": "dormakaba" }, method: "GET" })
      .then(res => res.json()).then(setStats).catch(() => setStats(null))
      .finally(() => setLoadingStats(false));
  }, []);

  const effectiveCounts = stats ? {
    installation: stats.total_installations, service: stats.total_services,
    sales: stats.total_sales, tickets: stats.total_tickets, ...counts,
  } : { installation: "-", service: "-", sales: "-", tickets: "-", ...counts };

  // ── Filter choices (city / status) ──────────────────────────────────────────

  useEffect(() => {
    const token = getToken();
    fetch(GET_INSTALLATION_CHOICES_ENDPOINT, {
      headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": localStorage.getItem("tenant") },
      method: "GET",
    })
      .then(res => res.json())
      .then(data => setFilterChoices({
        cities: data.cities || [],
        statuses: data.statuses || [],
        updated_by: data.updated_by || [],
      }))
      .catch(() => setFilterChoices({ cities: [], statuses: [], updated_by: [] }));
  }, []);

  // Accumulate cities/statuses/updated_by seen across fetches so the list never
  // shrinks once the table itself gets filtered down to a subset (ponytail:
  // in-memory accumulator, switch to a dedicated unfiltered choices endpoint if needed).
  const [seenCities, setSeenCities] = useState(new Set());
  const [seenStatuses, setSeenStatuses] = useState(new Set());
  const [seenUpdatedBy, setSeenUpdatedBy] = useState(new Set());

  useEffect(() => {
    setSeenCities(prev => {
      const next = new Set(prev);
      installationsData.forEach(i => i.address_details?.city && next.add(i.address_details.city));
      return next.size === prev.size ? prev : next;
    });
    setSeenStatuses(prev => {
      const next = new Set(prev);
      installationsData.forEach(i => i.status && next.add(i.status));
      return next.size === prev.size ? prev : next;
    });
    setSeenUpdatedBy(prev => {
      const next = new Set(prev);
      installationsData.forEach(i => i.updated_by && next.add(i.updated_by));
      return next.size === prev.size ? prev : next;
    });
  }, [installationsData]);

  const cityOptions = React.useMemo(() => {
    const names = Array.from(new Set([...filterChoices.cities.map(c => c.city_name), ...seenCities])).sort();
    return [{ label: "All Cities", value: "" }, ...names.map((c) => ({ label: c, value: c }))];
  }, [filterChoices.cities, seenCities]);

  const statusOptions = React.useMemo(() => {
    const names = Array.from(new Set([...(filterChoices.statuses || []), ...seenStatuses])).sort();
    return [{ label: "All Statuses", value: "" }, ...names.map((s) => ({ label: s, value: s }))];
  }, [filterChoices.statuses, seenStatuses]);

  const updatedByOptions = React.useMemo(() => {
    const names = Array.from(new Set([...(filterChoices.updated_by || []), ...seenUpdatedBy])).sort();
    return [{ label: "All Updated By", value: "" }, ...names.map((n) => ({ label: n, value: n }))];
  }, [filterChoices.updated_by, seenUpdatedBy]);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchInstallationsData = async () => {
    setLoadingTable(true); setError(null);
    const token = getToken();
    const params = new URLSearchParams();
    if (searchQuery)    params.append("search", searchQuery);
    if (cityFilter)     params.append("city", cityFilter);
    if (lockTypeFilter) params.append("lock_type", lockTypeFilter);
    if (statusFilter)   params.append("status", statusFilter);
    if (updatedByFilter)   params.append("updated_by", updatedByFilter);
    if (updatedDateFilter) params.append("updated_date", updatedDateFilter);
    let endpoint = `${GET_INSTALLATION_ENDPOINT}?limit=${rowsPerPage}&offset=${page * rowsPerPage}`;
    if (params.toString()) endpoint += `&${params.toString()}`;

    try {
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", "X-Tenant-Id": localStorage.getItem("tenant") },
      });
      if (!response.ok) throw new Error("Failed to fetch installations data");
      const jsonData = await response.json();
      setInstallationsData(jsonData.results || []);
      setTotalCount(jsonData.count || 0);
    } catch (error) {
      console.error("Error fetching installations data:", error.message);
      setError("Failed to load installations. Please try again.");
    } finally { setLoadingTable(false); }
  };

  useEffect(() => { fetchInstallationsData(); }, [page, rowsPerPage, searchQuery, cityFilter, lockTypeFilter, statusFilter, updatedByFilter, updatedDateFilter]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleSearch = (e) => {
    const value = e.target.value;
    if (value.length > 50) return;
    setSearchQuery(value); setCharCount(value.length); setPage(0);
  };

  const handleChangePage        = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  const handleEdit = (record) => {
    setEditFormData({
      installation_id: record.installation_id, ticket_code: record.ticket_code,
      customer_country_code: record.customer_country_code, status: record.status,
      customer_phone: record.customer_phone, wa_number: record.wa_number,
      customer_name: record.customer_name, wa_name: record.wa_name,
      lock_type: record.lock_type, lock_type_others: record.lock_type_others,
      door_type: record.door_type, door_installed: record.door_installed,
      scheduled_date: record.scheduled_date, address: { ...record.address_details },
      notes: record.notes || "",
    });
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    const token = getToken();
    try {
      const response = await fetch(`${PUT_INSTALLATION_ENDPOINT}${editFormData.installation_id}/`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", "X-Tenant-Id": localStorage.getItem("tenant") },
        body: JSON.stringify(editFormData),
      });
      if (response.ok) { setEditError(null); setEditSuccess(true); }
      else {
        const data = await response.json();
        const msgs = [];
        if (data?.ticket?.length)             msgs.push(`Ticket Error: ${data.ticket.join(" ")}`);
        if (data?.address?.length)            msgs.push(`Address Error: ${data.address.join(" ")}`);
        if (data?.customer?.length)           msgs.push(`Customer Error: ${data.customer.join(" ")}`);
        if (data?.customer_phone?.length)     msgs.push(`Phone Error: ${data.customer_phone.join(" ")}`);
        if (data?.customer_name?.length)      msgs.push(`Name Error: ${data.customer_name.join(" ")}`);
        if (data?.lock_type?.length)          msgs.push(`Lock Type Error: ${data.lock_type.join(" ")}`);
        if (data?.status?.length)             msgs.push(`Status Error: ${data.status.join(" ")}`);
        if (data?.issue_description?.length)  msgs.push(`Issue Description Error: ${data.issue_description.join(" ")}`);
        setEditError(msgs.length ? msgs.join(" | ") : "Failed to update. Please try again.");
      }
      fetchInstallationsData();
    } catch (error) {
      console.error("Error updating installation:", error.message);
      setError("Failed to update installation. Please try again.");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    const token = getToken();
    const params = new URLSearchParams();
    if (searchQuery)    params.append("search", searchQuery);
    if (cityFilter)     params.append("city", cityFilter);
    if (lockTypeFilter) params.append("lock_type", lockTypeFilter);
    if (statusFilter)   params.append("status", statusFilter);
    if (updatedByFilter)   params.append("updated_by", updatedByFilter);
    if (updatedDateFilter) params.append("updated_date", updatedDateFilter);
    let exportUrl = EXPORT_INSTALLATION_ENDPOINT;
    if (params.toString()) exportUrl += `?${params.toString()}`;
    try {
      const response = await fetch(exportUrl, { headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": localStorage.getItem("tenant") } });
      if (!response.ok) throw new Error("Failed to export data");
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = "installations.csv";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting data:", error.message);
      setError("Failed to export data. Please try again.");
    } finally { setExporting(false); }
  };

  // ── Column definitions ─────────────────────────────────────────────────────

  const handleView   = useCallback((item) => { setSelectedRecord(item); setModalOpen(true); }, []);
  const handleEditCb = useCallback((item) => handleEdit(item), []);

  const tableColumns = React.useMemo(() => [
    { id: "ticket_code",    label: "Ticket ID"       },
    { id: "customer_name",  label: "Customer Name"   },
    { id: "customer_phone", label: "Customer Phone"  },
    { id: "wa_name",        label: "WA Name"         },
    { id: "wa_number",      label: "WA Phone"        },
    { id: "city",           label: "City",           render: (item) => item.address_details?.city },
    { id: "lock_type",      label: "Lock Type"       },
    {
      id: "status",
      label: "Status",
      render: (item) => <Chip label={item.status} color={getStatusColor(item.status)} size="small" />,
    },
    { id: "updated_at", label: "Updated At", render: (item) => formatWithTzLabel(item.updated_at, "DD/MM/YYYY, hh:mm A") },
    { id: "updated_by", label: "Updated By"  },
    {
      id: "__actions__",
      label: "Actions",
      render: (item) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleView(item); }}>
            <EyeIcon />
          </IconButton>
          <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); handleEditCb(item); }}>
            <EditIcon />
          </IconButton>
        </Box>
      ),
    },
  ], [handleView, handleEditCb]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const DetailRow = ({ label, value, fullWidth = false, preWrap = false, chip = false }) => (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>{label}</Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        {chip
          ? <Chip label={value} color={getStatusColor(value)} size="small" />
          : <Typography variant="body2" style={preWrap ? { whiteSpace: "pre-line" } : undefined}>{value}</Typography>}
      </Paper>
    </Grid>
  );

  return (
    <DoorLocksFeatureLayout
      activeTab={tab} onTabChange={handleTabChange} onBack={handleBack}
      counts={effectiveCounts} loadingStats={loadingStats}
    >
      {loadingStats ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Filter bar */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Box display="flex" alignItems="center" flexWrap={{ xs: "wrap", lg: "nowrap" }} sx={{ width: "100%", minWidth: 0, gap: 1, flexWrap: "wrap", alignItems: "center" }}>
             <Box sx={{ flex: "1 1 280px", minWidth: 220 }}>
                <TableSearchInput
                  searchQuery={searchQuery}
                  onSearchChange={handleSearch}
                  tooltip="Search by Ticket ID, Customer Phone, Customer Name, City"
                  placeholder="Search by Ticket ID, Customer Phone, Customer Name, City "
                  charCount={charCount}
                />
              </Box>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={lockTypeFilter}
                onMenuSelect={(value) => { setLockTypeFilter(value); setPage(0); }}
                menuItems={LOCK_TYPE_OPTIONS}
                sx={{ minWidth: 140, whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {LOCK_TYPE_OPTIONS.find(o => o.value === lockTypeFilter)?.label ?? "All Lock Types"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={cityFilter}
                onMenuSelect={(value) => { setCityFilter(value); setPage(0); }}
                menuItems={cityOptions}
                sx={{ minWidth: 130, whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {cityOptions.find(o => o.value === cityFilter)?.label ?? "All Cities"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={statusFilter}
                onMenuSelect={(value) => { setStatusFilter(value); setPage(0); }}
                menuItems={statusOptions}
                sx={{ minWidth: 130, whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {statusOptions.find(o => o.value === statusFilter)?.label ?? "All Statuses"}
              </AppDropdown>

              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: grayFontColor }} />}
                selectedValue={updatedByFilter}
                onMenuSelect={(value) => { setUpdatedByFilter(value); setPage(0); }}
                menuItems={updatedByOptions}
                sx={{ minWidth: 150, whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {updatedByOptions.find(o => o.value === updatedByFilter)?.label ?? "All Updated By"}
              </AppDropdown>

              <Box sx={{ width: 175, flexShrink: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <AppDatePicker
                    label="Updated Date"
                    fullWidth
                    value={updatedDateFilter ? dayjs(updatedDateFilter) : null}
                    onChange={(newValue) => { setUpdatedDateFilter(newValue ? newValue.format("YYYY-MM-DD") : ""); setPage(0); }}
                  />
                </LocalizationProvider>
              </Box>

              <ButtonOne
                variant="contained"
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                onClick={() => { setSearchQuery(""); setCityFilter(""); setLockTypeFilter(""); setStatusFilter(""); setUpdatedByFilter(""); setUpdatedDateFilter(""); setCharCount(0); setPage(0); }}
              >
                Reset
              </ButtonOne>

              <ButtonTwo
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={loadingTable || exporting}
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {exporting ? "Exporting..." : "Export CSV"}
              </ButtonTwo>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          <AppTable
            columns={tableColumns}
            rows={installationsData}
            rowKey="installation_id"
            loading={loadingTable}
            emptyMessage="No installations found"
            maxHeight={440}
            headCellSx={{ backgroundColor: "#f5f5f5" }}
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />

          {/* View Details Modal */}
          <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Installation Details</DialogTitle>
            <DialogContent dividers>
              {selectedRecord && (
                <Grid container spacing={2}>
                  <DetailRow label="Ticket ID"      value={selectedRecord.ticket_code}               />
                  <DetailRow label="Customer Name"  value={selectedRecord.customer_name}             />
                  <DetailRow label="WA Name"        value={selectedRecord.wa_name}                   />
                  <DetailRow label="Phone"          value={selectedRecord.customer_phone}            />
                  <DetailRow label="WA Phone"       value={selectedRecord.wa_number}                 />
                  <DetailRow label="Door Type"      value={selectedRecord.door_type}                 />
                  <DetailRow label="Lock Type"      value={selectedRecord.lock_type}                 />
                  {selectedRecord.lock_type === "Others" && (
                    <DetailRow label="Lock Type - Description" value={selectedRecord.lock_type_others} />
                  )}
                  <DetailRow label="Door Installed" value={selectedRecord.door_installed ? "Yes" : "No"} />
                  <DetailRow label="Status"         value={selectedRecord.status} chip />
                  {selectedRecord.address_details && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Address</Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body2">
                          {selectedRecord.address_details.flat}, {selectedRecord.address_details.street}<br />
                          {selectedRecord.address_details.area}<br />
                          {selectedRecord.address_details.city}, {selectedRecord.address_details.state}<br />
                          PIN: {selectedRecord.address_details.pincode}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {selectedRecord.notes && (
                    <DetailRow label="Notes" value={selectedRecord.notes} fullWidth preWrap />
                  )}
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setModalOpen(false)} color="primary">Close</Button>
            </DialogActions>
          </Dialog>

          {/* Edit Modal */}
          <InstallationEditModal
            open={editModalOpen}
            onClose={() => { setEditModalOpen(false); setEditError(null); setEditSuccess(false); }}
            formData={editFormData}
            onInputChange={(field, value) => setEditFormData(prev => ({ ...prev, [field]: value }))}
            onAddressChange={(field, value) => setEditFormData(prev => ({ ...prev, address: { ...prev.address, [field]: value } }))}
            onUpdate={handleUpdate}
            error={editError}
            success={editSuccess}
          />
        </>
      )}
    </DoorLocksFeatureLayout>
  );
}